/**
 * Pure logic for the SUSPECT swipe deck (Tinder-mode flag review, 2026-07-05
 * owner UAT). Distinct from swipeDeckLogic.ts, which owns the generic gesture
 * constants (classifySwipe/lockAxis — reused by the deck component) and the
 * keep/exclude deck's helpers. This module owns the suspect QUEUE semantics.
 */
import type { Suspect } from "./strokeSuspects";
import type { SetGroup } from "./setGroups";
import type { Lap } from "./laps";
import { setIndexForLap } from "./setMath";

export interface SetPosition {
  /** 1-based position of the length within its set. */
  position: number;
  /** Total lengths in the set (member laps, matching SetDetailSheet). */
  total: number;
}

export interface DeckItem {
  suspect: Suspect;
  setIndex: number | null;
  pos: SetPosition | null;
}

/** The remaining review queue: unresolved suspects (already resolution-filtered
 *  upstream in deriveWorkout) minus this session's skips, in lapIndex order. */
export function deckQueue(
  suspects: Suspect[],
  setGroups: SetGroup[],
  skipped: ReadonlySet<number>,
): DeckItem[] {
  return suspects
    .filter((s) => !skipped.has(s.lapIndex))
    .map((s) => {
      const setIndex = setIndexForLap(setGroups, s.lapIndex);
      return { suspect: s, setIndex, pos: setPositionForLap(setGroups, setIndex, s.lapIndex) };
    });
}

/** The flagged length's ordinal position within its set ("2nd of 8"), or null
 *  when the lap isn't in any set group. Uses the set's OWN laps order (time
 *  order, all members) so it matches what SetDetailSheet shows (lapPos + 1). */
export function setPositionForLap(
  setGroups: SetGroup[],
  setIndex: number | null,
  lapIndex: number,
): SetPosition | null {
  if (setIndex === null) return null;
  const set = setGroups[setIndex];
  if (!set) return null;
  const pos = set.laps.findIndex((l) => l.index === lapIndex);
  if (pos === -1) return null;
  return { position: pos + 1, total: set.laps.length };
}

/** "1st" / "2nd" / "3rd" / "4th" … jargon-free ordinal. */
export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/**
 * "3 of 5" — the card being shown counts as in-progress. The denominator is
 * resolved + remaining + skipped so a mid-session Skip never shrinks the
 * total (skipped cards are still "in the deck", just deferred — see B1).
 * When `remaining` is 0 the session is over (skips aside, nothing is being
 * shown), so the label reports the plain resolved count rather than
 * clamping to resolved+1, which would overstate progress by one.
 */
export function progressLabel(resolved: number, remaining: number, skipped = 0): string {
  const total = resolved + remaining + skipped;
  if (total === 0) return "";
  if (remaining === 0) return `${resolved} of ${total}`;
  return `${Math.min(resolved + 1, total)} of ${total}`;
}

/** Tile-length stroke names. Future home: the D7 strokeVocabulary module. */
const SHORT: Record<string, string> = {
  Freestyle: "Free",
  Backstroke: "Back",
  Breaststroke: "Breast",
  Butterfly: "Fly",
};
export function shortStroke(stroke: string): string {
  return SHORT[stroke] ?? stroke;
}

/**
 * Deck-card meta seconds — whole seconds render bare ("40s"); anything with a
 * fractional part keeps one decimal ("26.1s", caller appends "s"). Deck-card
 * only: unlike fmtSeconds1 (set detail sheet, which deliberately pads whole
 * numbers to X.0), the compact card meta line reads cleaner without the
 * trailing ".0".
 */
export function fmtCardSeconds(seconds: number): string {
  const rounded = Math.round(seconds * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
}

/** One chip in the deck card's "Set Memory Strip" (wave-1 Option A). Color is
 *  a rendering concern (the component maps `stroke` through the shared
 *  strokeColor() token) — this stays a plain data shape so it's cheap to test. */
export interface StripChip {
  lapIndex: number;
  stroke: string;
  flagged: boolean;
}

export interface MemoryStrip {
  chips: StripChip[];
  /** Earlier lengths hidden behind a leading "+N" chip; 0 = none hidden. */
  overflowBefore: number;
  /** Later lengths hidden behind a trailing "+N" chip; 0 = none hidden. */
  overflowAfter: number;
}

/** Max chips rendered before overflowing to a "+N" summary chip — matches
 *  SetRibbon's MAX_CHIPS (views/SetRibbon.svelte) so the deck strip and the
 *  ribbon agree on when a set counts as "long". */
export const STRIP_MAX_CHIPS = 22;

/**
 * Whole-set memory strip for the fix-strokes card (wave-1 Option A): one chip
 * per length in `laps` (time order, the set's OWN laps — same source
 * SetRibbon and SetDetailSheet read), with the flagged length marked.
 *
 * Sets at or under STRIP_MAX_CHIPS show every length, no overflow. Longer
 * sets window down to STRIP_MAX_CHIPS chips:
 *   - flagged length inside the natural front window → keep it (matches
 *     SetRibbon's plain "first N, then +N more" behavior).
 *   - flagged length inside the natural tail window → shift the window to
 *     the tail so the flagged chip isn't buried behind a "+N more" the card
 *     is asking the owner to look at (panel must-fix #4).
 *   - flagged length far from both ends of a very long set → center the
 *     window on it; both ends overflow.
 * The flagged length is NEVER hidden — that is the whole point of the pin.
 */
export function buildMemoryStrip(
  laps: Pick<Lap, "index" | "strokeStyle">[],
  flaggedLapIndex: number,
): MemoryStrip {
  const total = laps.length;
  const flaggedPos = laps.findIndex((l) => l.index === flaggedLapIndex);

  let start = 0;
  let end = total;
  if (total > STRIP_MAX_CHIPS) {
    if (flaggedPos === -1 || flaggedPos < STRIP_MAX_CHIPS) {
      start = 0;
      end = STRIP_MAX_CHIPS;
    } else if (flaggedPos >= total - STRIP_MAX_CHIPS) {
      start = total - STRIP_MAX_CHIPS;
      end = total;
    } else {
      start = Math.max(0, Math.min(flaggedPos - Math.floor(STRIP_MAX_CHIPS / 2), total - STRIP_MAX_CHIPS));
      end = start + STRIP_MAX_CHIPS;
    }
  }

  const chips: StripChip[] = laps.slice(start, end).map((l) => ({
    lapIndex: l.index,
    stroke: l.strokeStyle,
    flagged: l.index === flaggedLapIndex,
  }));
  return { chips, overflowBefore: start, overflowAfter: total - end };
}
